import Vegliste from "../interfaces/Vegliste.interface"
import { Codec, string, number, exactly, oneOf, GetInterface } from "purify-ts"
import { AreaMappings } from "../utils/setFylkerOgKommuner"
import { Omraader } from "@nvdb-rapport/shared/src/components/AreaSelect"
import { RemoteData } from "@nvdb-rapport/shared/src/utils/remoteData"
import * as R from "remeda"

export enum AppState {
  DETECTING_BROWSER = "DETECTING_BROWSER",
  UNSUPPORTED_BROWSER = "UNSUPPORTED_BROWSER",
  LOADING = "LOADING",
  APP_READY = "APP_READY",
  DATA_PENDING = "DATA_PENDING",
  DATA_READY = "DATA_READY",
  ERROR = "ERROR",
}

// Error interface
interface ErrorData {
  message: string
  title: string
}

export const PollData = Codec.interface({
  id: string,
  durationMs: number,
  durationMsString: string,
  status: oneOf([
    exactly("PENDING"),
    exactly("ERROR"),
    exactly("SUCCESS"),
    exactly("UNKNOWN"),
  ]),
  error: string,
})

export type PollData = GetInterface<typeof PollData>

export type Language = "nb_NO" | "nn_NO"

// Search Result interface
interface SearchResult {
  // Table to display
  tableData: Vegliste | null

  //  Link to file
  downloadLinkXLSX: string
  downloadLinkDOCX: string
  downloadLinkCSV: string
  //
  durationMs: number
  durationMsString: string
}

export interface UIModel {
  appState: AppState
  errors: ErrorData[]
  areaMappings: AreaMappings | undefined
  omraader: RemoteData<Error, Omraader>
  jobId: string | undefined
  searchResult: SearchResult
  language: Language
}

const initialAreaMappings: AreaMappings = {
  fylkeNrTilKommuneMap: new Map<number, Map<string, number>>(),
  fylkeNrTilFylkeNavn: new Map<number, string>(),
  fylkeNavnTilFylkeNr: new Map<string, number>(),
  kommuneNavnTilNr: new Map<string, number>(),
} as AreaMappings

export const initialUIModel: UIModel = {
  language: "nb_NO",
  appState: AppState.LOADING,
  omraader: { type: "LOADING" },
  errors: [],
  areaMappings: initialAreaMappings,
  jobId: undefined,
  searchResult: {
    tableData: null,
    downloadLinkXLSX: "",
    downloadLinkDOCX: "",
    downloadLinkCSV: "",
    durationMs: 0,
    durationMsString: "",
  },
}

type SetAreaMappings = { type: "SetAreaMappings"; payload: AreaMappings }
type UpdateAppState = { type: "UpdateAppState"; payload: AppState }
type AddError = { type: "AddError"; payload: ErrorData }
type JobPending = { type: "JobPending"; payload: string }
type Poll = { type: "Poll"; payload: PollData }
type SetSearchResult = { type: "SetSearchResult"; payload: SearchResult }
type ClearSearchResult = { type: "ClearSearchResult" }
type RequestData = { type: "RequestData" }
type SetLanguage = { type: "SetLanguage"; payload: Language }
type SetOmraader = { type: "SetOmraader"; payload: RemoteData<Error, Omraader> }

export type UIAction =
  | SetAreaMappings
  | UpdateAppState
  | AddError
  | JobPending
  | Poll
  | SetSearchResult
  | ClearSearchResult
  | RequestData
  | SetLanguage
  | SetOmraader

export function uiReducer(model: UIModel, action: UIAction): UIModel {
  switch (action.type) {
    case "SetAreaMappings":
      return { ...model, areaMappings: action.payload }
    case "UpdateAppState":
      return { ...model, appState: action.payload }
    case "AddError":
      return {
        ...model,
        errors: [...model.errors, action.payload],
        appState: AppState.ERROR,
      }
    case "JobPending":
      return {
        ...model,
        appState: AppState.DATA_PENDING,
        jobId: action.payload,
      }
    case "Poll":
      return {
        ...model,
        searchResult: {
          ...model.searchResult,
          durationMsString: action.payload.durationMsString,
          durationMs: action.payload.durationMs,
        },
      }
    case "SetSearchResult":
      return { ...model, searchResult: action.payload }
    case "ClearSearchResult":
      return {
        ...model,
        searchResult: initialUIModel.searchResult,
        appState: AppState.APP_READY,
      }
    case "RequestData":
      return { ...model, appState: AppState.DATA_PENDING, errors: [] }
    case "SetLanguage":
      return { ...model, language: action.payload }

    case "SetOmraader":
      return R.pipe(model, R.set("omraader", action.payload))
  }
}

export type UIStateProps = { uiState: UIModel }
